import { useMutation, useQuery } from '@tanstack/react-query';
import * as tienda from '@/api/tienda.api';
import type { Venta } from '@/api/tienda.api';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';

/**
 * DE QUÉ LUGAR SALE LA VENTA.
 *
 * En un borrador se puede cambiar: las ventas que arma el agente desde el chat nacen sin lugar
 * (o sea, del almacén) y quien confirma dice antes de qué tienda sale. Confirmada, ya descontó
 * de su lugar y solo se lee.
 */
export function LugarDeLaVenta({ venta, onCambio }: { venta: Venta; onCambio: () => void }) {
  const toast = useToast();
  const { data: lugares } = useQuery({
    queryKey: ['inventario', 'ubicaciones'],
    queryFn: tienda.listarUbicaciones,
    enabled: venta.estado === 'BORRADOR',
  });
  const cambiar = useMutation({
    mutationFn: (ubicacionId: string) => tienda.ponerLugarDeVenta(venta.id, ubicacionId),
    onSuccess: (v) => {
      toast.success(`La venta sale de ${v.ubicacion?.nombre ?? 'el almacén'}.`);
      onCambio();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo cambiar el lugar'),
  });

  if (venta.estado !== 'BORRADOR' || !lugares) {
    // Las ventas de antes de que existiera el lugar salieron del total, sin decir de dónde.
    return <span>{venta.ubicacion?.nombre ?? (venta.estado === 'BORRADOR' ? 'Almacén' : 'Sin lugar anotado')}</span>;
  }
  const actual = venta.ubicacionId ?? lugares.find((u) => u.tipo === 'ALMACEN')?.id ?? '';
  return (
    <Select
      aria-label="De qué lugar sale la venta"
      className="h-9 w-auto min-w-[10rem]"
      value={actual}
      disabled={cambiar.isPending}
      onChange={(e) => cambiar.mutate(e.target.value)}
    >
      {lugares.map((u) => (
        <option key={u.id} value={u.id}>
          {u.nombre}
        </option>
      ))}
    </Select>
  );
}
